import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { clsx } from 'clsx';

const plans = [
  {
    name: 'Free',
    description: 'Para começar a organizar suas finanças',
    price: { monthly: 0, yearly: 0 },
    features: [
      { name: 'Até 2 contas bancárias', included: true },
      { name: '50 transações/mês', included: true },
      { name: '5 categorias', included: true },
      { name: 'Dashboard básico', included: true },
      { name: 'Recorrências', included: false },
      { name: 'Parcelamentos', included: false },
      { name: 'Investimentos', included: false },
      { name: 'Simulação', included: false },
      { name: 'Relatórios', included: false },
      { name: 'Exportação', included: false },
      { name: 'Niveis e conquistas', included: true },
      { name: 'Programa de indicacao', included: true },
    ],
    cta: 'Começar Grátis',
    highlighted: false,
  },
  {
    name: 'Pro',
    description: 'Para quem quer controle total',
    price: { monthly: 19.90, yearly: 199.00 },
    features: [
      { name: 'Contas ilimitadas', included: true },
      { name: 'Transações ilimitadas', included: true },
      { name: 'Categorias ilimitadas', included: true },
      { name: 'Dashboard completo', included: true },
      { name: 'Recorrências', included: true },
      { name: 'Parcelamentos', included: true },
      { name: 'Investimentos', included: true },
      { name: 'Simulação', included: true },
      { name: 'Relatórios', included: true },
      { name: 'Exportação CSV/PDF', included: true },
      { name: 'Niveis e conquistas', included: true },
      { name: 'Programa de indicacao', included: true },
    ],
    cta: 'Assinar Pro',
    highlighted: true,
  },
  {
    name: 'Família',
    description: 'Para compartilhar com a família (até 5 usuários)',
    price: { monthly: 39.90, yearly: 399.00 },
    features: [
      { name: 'Tudo do Pro', included: true },
      { name: 'Até 5 usuários', included: true },
      { name: 'Contas compartilhadas', included: true },
      { name: 'Dashboard família', included: true },
      { name: 'Metas conjuntas', included: true },
      { name: 'Relatórios família', included: true },
      { name: 'Permissões por usuário', included: true },
      { name: 'Suporte prioritário', included: true },
      { name: 'Niveis e conquistas', included: true },
      { name: 'Programa de indicacao', included: true },
    ],
    cta: 'Assinar Família',
    highlighted: false,
  },
];

const Pricing = () => {
  const [isYearly, setIsYearly] = useState(false);

  return (
    <div className="py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Planos e Preços
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-8">
            Escolha o plano ideal para suas necessidades. Comece grátis e faça upgrade quando quiser.
          </p>

          {/* Toggle */}
          <div className="flex items-center justify-center gap-4">
            <span className={clsx('text-sm font-medium', !isYearly ? 'text-gray-900 dark:text-white' : 'text-gray-500')}>
              Mensal
            </span>
            <button
              onClick={() => setIsYearly(!isYearly)}
              className={clsx(
                'relative w-14 h-7 rounded-full transition-colors',
                isYearly ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-700'
              )}
            >
              <span
                className={clsx(
                  'absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform',
                  isYearly ? 'translate-x-8' : 'translate-x-1'
                )}
              />
            </button>
            <span className={clsx('text-sm font-medium', isYearly ? 'text-gray-900 dark:text-white' : 'text-gray-500')}>
              Anual <span className="text-green-600 font-bold">-17%</span>
            </span>
          </div>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={clsx(
                'rounded-2xl p-8 border-2 transition-all',
                plan.highlighted
                  ? 'border-primary-600 shadow-xl scale-105 relative'
                  : 'border-gray-200 dark:border-gray-800'
              )}
            >
              {plan.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary-600 text-white text-sm font-medium rounded-full">
                  Mais Popular
                </div>
              )}

              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {plan.name}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {plan.description}
              </p>

              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900 dark:text-white">
                  {plan.price.monthly === 0 ? 'Grátis' : `R$ ${isYearly ? (plan.price.yearly / 12).toFixed(2) : plan.price.monthly.toFixed(2)}`}
                </span>
                {plan.price.monthly > 0 && (
                  <span className="text-gray-500 dark:text-gray-400">/mês</span>
                )}
                {isYearly && plan.price.yearly > 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    R$ {plan.price.yearly.toFixed(2)} cobrado anualmente
                  </p>
                )}
              </div>

              <Link
                to="/register"
                className={clsx(
                  'block w-full py-3 rounded-xl font-semibold text-center transition-colors',
                  plan.highlighted
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700'
                )}
              >
                {plan.cta}
              </Link>

              <ul className="mt-8 space-y-4">
                {plan.features.map((feature) => (
                  <li key={feature.name} className="flex items-center gap-3">
                    {feature.included ? (
                      <Check className="text-green-500 flex-shrink-0" size={20} />
                    ) : (
                      <X className="text-gray-300 dark:text-gray-600 flex-shrink-0" size={20} />
                    )}
                    <span className={feature.included ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-600'}>
                      {feature.name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Pricing;
